import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { OpsTask, OpsCategory, CRMUser } from '../../types';
import StatusPill from '../pm/StatusPill';
import PriorityBadge from '../pm/PriorityBadge';
import { RefreshCw, Flag, X, ArrowLeft, ListChecks } from 'lucide-react';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  getActionRequiredShortLabel,
  isActionRequiredExternal,
} from '../../utils/actionRequiredDisplay';
import { opsOriginHref, opsOriginLabel } from '../../utils/opsOriginDisplay';
import UserAvatar from '../ui/UserAvatar';
import { filterOpsTaskEmployeeTeam } from '../../utils/userRoles';

const CAT_LABELS: Record<OpsCategory, string> = {
  admin: 'Admin', maintenance: 'Maint.', ordering: 'Order', requests: 'Req.', other: 'Other',
};
const CAT_COLORS: Record<OpsCategory, string> = {
  admin: '#6366F1', maintenance: '#F59E0B', ordering: '#0891B2', requests: '#8B5CF6', other: '#6B7280',
};

interface OpsTaskRowProps {
  task: OpsTask;
  teamMembers: CRMUser[];
  onSelect: () => void;
  onUpdate: (updates: Partial<OpsTask>) => void;
  animDelay?: number;
  showCategory?: boolean;
}

const OpsTaskRow: React.FC<OpsTaskRowProps> = ({ task, teamMembers, onSelect, onUpdate, animDelay = 0, showCategory }) => {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuStep, setActionMenuStep] = useState<'list' | 'external'>('list');
  const [externalDraft, setExternalDraft] = useState('');
  const actionRef = useRef<HTMLDivElement>(null);
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
  const isOnHold = task.status === 'on-hold';
  const assignableTeam = useMemo(() => filterOpsTaskEmployeeTeam(teamMembers), [teamMembers]);
  const assignee = task.assignedTo?.[0] ? teamMembers.find(u => u.uid === task.assignedTo[0]) : null;
  const originLabel = opsOriginLabel(task.origin);
  const originHref = opsOriginHref(task);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (actionRef.current && !actionRef.current.contains(e.target as Node)) setShowActionMenu(false);
    };
    if (showActionMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showActionMenu]);

  useEffect(() => {
    if (!showActionMenu) {
      setActionMenuStep('list');
      setExternalDraft('');
    }
  }, [showActionMenu]);

  const fmtDate = (d: Date) => {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdate({ status: task.status === 'completed' ? 'pending' : 'completed' });
  };

  return (
    <div
      className={`ops-row ${task.status === 'completed' ? 'completed' : ''} ${isOverdue ? 'overdue' : ''} ${isOnHold ? 'on-hold' : ''} ${showActionMenu || statusMenuOpen || priorityMenuOpen ? 'ops-row--menu-open' : ''}`}
      style={{ animationDelay: `${animDelay}ms` }}
      onClick={onSelect}
    >
      <div className="ops-row-title-cell">
        <button className={`ops-row-check ${task.status === 'completed' ? 'checked' : ''}`} onClick={handleToggle}>
          {task.status === 'completed' && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
          )}
        </button>
        {showCategory && (
          <span className="ops-row-cat-tag" style={{ background: CAT_COLORS[task.category] + '18', color: CAT_COLORS[task.category], borderColor: CAT_COLORS[task.category] + '30' }}>
            {CAT_LABELS[task.category]}
          </span>
        )}
        <span className="ops-row-title">{task.title}</span>
        {originLabel && (
          originHref ? (
            <Link
              to={originHref}
              className={`ops-origin-badge ops-origin-${task.origin?.kind || 'manual'}`}
              onClick={(e) => e.stopPropagation()}
              title={originLabel}
            >
              {originLabel}
            </Link>
          ) : (
            <span className={`ops-origin-badge ops-origin-${task.origin?.kind || 'manual'}`} title={originLabel}>
              {originLabel}
            </span>
          )
        )}
        {task.recurring && <RefreshCw size={11} className="ops-row-recurring" />}
        {task.subtasks && task.subtasks.length > 0 && (
          <span
            className="ops-row-checklist"
            title={`Checklist ${task.subtasks.filter((s) => s.completed).length}/${task.subtasks.length}`}
          >
            <ListChecks size={11} aria-hidden />
            {task.subtasks.filter((s) => s.completed).length}/{task.subtasks.length}
          </span>
        )}
      </div>
      <div
        className={`ops-row-status${statusMenuOpen ? ' pm-dropdown-host--open' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <StatusPill
          status={task.status}
          onChange={(s) => onUpdate({ status: s })}
          onOpenChange={setStatusMenuOpen}
        />
      </div>
      <div
        className={`ops-row-priority${priorityMenuOpen ? ' pm-dropdown-host--open' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <PriorityBadge
          priority={task.priority}
          onChange={(p) => onUpdate({ priority: p })}
          onOpenChange={setPriorityMenuOpen}
        />
      </div>
      <div className="ops-row-assignee">
        {assignee ? (
          <UserAvatar
            className="ops-row-avatar"
            name={assignee.displayName}
            photoURL={assignee.photoURL}
            userId={assignee.uid}
          />
        ) : (
          <span className="ops-row-no-assignee">—</span>
        )}
      </div>
      <div className={`ops-row-due ${isOverdue ? 'ops-row-due-overdue' : ''}`}>
        {task.dueDate ? fmtDate(task.dueDate) : '—'}
      </div>
      <div
        className={`ops-row-action${showActionMenu ? ' pm-dropdown-host--open' : ''}`}
        ref={actionRef}
        onClick={e => e.stopPropagation()}
      >
        {task.actionRequired ? (
          <div
            className={`ops-action-flag ${isActionRequiredExternal(task.actionRequired) ? 'ops-action-flag--external' : ''}`}
            onClick={() => setShowActionMenu(!showActionMenu)}
            title={
              isActionRequiredExternal(task.actionRequired)
                ? (task.actionRequiredNote || 'External — action required')
                : task.actionRequiredNote || 'Action required'
            }
          >
            <Flag size={10} />
            {getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, teamMembers)}
          </div>
        ) : (
          <button
            className="ops-action-set-btn"
            onClick={() => setShowActionMenu(!showActionMenu)}
            title="Set action required"
          >
            <Flag size={12} />
          </button>
        )}
        {showActionMenu && (
          <div className="pm-dropdown" style={{ right: 0, left: 'auto', minWidth: actionMenuStep === 'external' ? 220 : 180 }}>
            {actionMenuStep === 'external' ? (
              <div className="pm-action-external-panel" onClick={e => e.stopPropagation()}>
                <button
                  type="button"
                  className="pm-dropdown-item pm-action-external-back"
                  onClick={() => { setActionMenuStep('list'); setExternalDraft(''); }}
                >
                  <ArrowLeft size={12} /> Back
                </button>
                <label className="pm-action-external-label">Who we&apos;re waiting on</label>
                <input
                  className="pm-action-external-input"
                  value={externalDraft}
                  onChange={e => setExternalDraft(e.target.value)}
                  placeholder="e.g. county inspector, vendor"
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter' && externalDraft.trim()) {
                      onUpdate({
                        actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                        actionRequiredNote: externalDraft.trim(),
                      });
                      setShowActionMenu(false);
                    }
                  }}
                />
                <button
                  type="button"
                  className="pm-action-external-save"
                  disabled={!externalDraft.trim()}
                  onClick={() => {
                    if (!externalDraft.trim()) return;
                    onUpdate({
                      actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                      actionRequiredNote: externalDraft.trim(),
                    });
                    setShowActionMenu(false);
                  }}
                >
                  Save
                </button>
              </div>
            ) : (
              <>
                {task.actionRequired && (
                  <button className="pm-dropdown-item" onClick={() => { onUpdate({ actionRequired: undefined, actionRequiredNote: undefined } as any); setShowActionMenu(false); }}>
                    <X size={12} /> Clear Action Required
                  </button>
                )}
                {assignableTeam.map(u => (
                  <button
                    key={u.uid}
                    className="pm-dropdown-item"
                    onClick={() => { onUpdate({ actionRequired: u.uid, actionRequiredNote: undefined }); setShowActionMenu(false); }}
                  >
                    <UserAvatar className="ops-row-avatar" name={u.displayName} photoURL={u.photoURL} userId={u.uid} style={{ width: 20, height: 20, fontSize: 8 }} />
                    {u.displayName}
                  </button>
                ))}
                <button
                  type="button"
                  className="pm-dropdown-item pm-dropdown-item--external"
                  onClick={() => {
                    setActionMenuStep('external');
                    setExternalDraft(
                      task.actionRequired && isActionRequiredExternal(task.actionRequired)
                        ? (task.actionRequiredNote || '').trim()
                        : '',
                    );
                  }}
                >
                  <Flag size={10} style={{ color: '#047857' }} />
                  External…
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpsTaskRow;
